import React, { useState, useEffect } from 'react';
import { getProducts, updateProductStock } from '../services/apiServices';
import styles from './EditStockModal.module.css';

const EditStockModal = ({ isOpen, onClose }) => {
  const [productsData, setProductsData] = useState({});
  const [editedStock, setEditedStock] = useState({}); // { productName: newStockValue }
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({}); // { productName: boolean }
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const fetchProducts = async () => {
        setLoading(true);
        setError(null);
        try {
          const data = await getProducts();
          setProductsData(data);
          // Inicializa o estado de edição com os valores atuais do estoque
          const initialEditedStock = {};
          Object.entries(data).forEach(([name, details]) => {
            initialEditedStock[name] = details.stock;
          });
          setEditedStock(initialEditedStock);
        } catch (err) {
          console.error("Erro ao carregar produtos para edição:", err);
          setError('Falha ao carregar produtos para edição.');
        } finally {
          setLoading(false);
        }
      };
      fetchProducts();
    } else {
      // Limpa os estados ao fechar o modal
      setProductsData({});
      setEditedStock({});
      setLoading(false);
      setSavingStates({});
      setError(null);
    }
  }, [isOpen]);

  const handleStockChange = (productName, value) => {
    setEditedStock(prev => ({
      ...prev,
      [productName]: value === '' ? '' : parseInt(value, 10), // Permite campo vazio temporariamente
    }));
  };

  // Funções para os botões de mais e menos
  const handleIncrement = (productName) => {
    setEditedStock(prev => ({
      ...prev,
      [productName]: (parseInt(prev[productName], 10) || 0) + 1,
    }));
  };

  const handleDecrement = (productName) => {
    setEditedStock(prev => ({
      ...prev,
      [productName]: Math.max(0, (parseInt(prev[productName], 10) || 0) - 1),
    }));
  };

  const handleSaveItem = async (productName) => {
    setSavingStates(prev => ({ ...prev, [productName]: true }));
    setError(null);

    try {
      const newStock = editedStock[productName];
      if (newStock !== '' && !isNaN(newStock)) {
        await updateProductStock(productName, parseInt(newStock, 10));
        // A atualização visual dos produtos virá pelo socket,
        // então não precisamos fechar o modal ou recarregar os dados aqui.
      }
    } catch (err) {
      console.error(`Erro ao salvar o estoque de ${productName}:`, err);
      alert(`Falha ao salvar o estoque de ${productName}.`);
    } finally {
      setSavingStates(prev => ({ ...prev, [productName]: false }));
    }
  };

  if (!isOpen) return null;

  const categories = ['assados', 'acompanhamentos', 'bebidas'];
  const categoryNames = {
    assados: '🍖 Assados',
    acompanhamentos: '🥗 Acompanhamentos',
    bebidas: '🥤 Bebidas',
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2>Editar Estoque de Produtos</h2>
        {loading && <p>Carregando produtos...</p>}
        {error && <p className={styles.error}>{error}</p>}
        {!loading && !error && (
          <div className={styles.productsGrid}>
            {categories.map(categoryKey => {
              const productsInCategory = Object.entries(productsData)
                .filter(([, details]) => details.category === categoryKey)
                .sort(([a], [b]) => a.localeCompare(b));

              if (productsInCategory.length === 0) return null;

              return (
                <div key={categoryKey} className={`${styles.categoryColumn} ${styles[categoryKey]}`}>
                  <h3>{categoryNames[categoryKey]}</h3>
                  {productsInCategory.map(([name, details]) => (
                    <div key={name} className={styles.productRow}>
                      <div className={styles.productItem}>
                        <span className={styles.productName}>{name}</span>
                        <div className={styles.quantityControl}>
                          <button
                            type="button"
                            onClick={() => handleDecrement(name)}
                            className={styles.quantityBtn}
                            disabled={savingStates[name]}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            value={editedStock[name] ?? ''}
                            onChange={(e) => handleStockChange(name, e.target.value)}
                            min="0"
                            className={styles.stockInput}
                            disabled={savingStates[name]}
                            autoComplete="off"
                          />
                          <button
                            type="button"
                            onClick={() => handleIncrement(name)}
                            className={styles.quantityBtn}
                            disabled={savingStates[name]}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => handleSaveItem(name)}
                        className={styles.saveItemBtn}
                        disabled={savingStates[name] || editedStock[name] === details.stock}
                      >
                        {savingStates[name] ? '...' : '✓'}
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
        <div className={styles.modalActions}>
          <button onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default EditStockModal;